import React, { useState, useEffect } from "react";
import {
  Grid,
  Button,
  MenuItem,
  Typography,
  Paper,
  Card,
  CardHeader,
  CardContent,
  Box,
  Chip,
  Avatar,
  Divider,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  Checkbox,
  ListItemText,
  Fade,
  Tooltip,
  IconButton,
} from "@material-ui/core";
import {
  makeStyles,
  createTheme,
  ThemeProvider,
} from "@material-ui/core/styles";
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  GetApp as DownloadIcon,
  Refresh as RefreshIcon,
  Person as PersonIcon,
  Group as GroupIcon,
  InfoOutlined as InfoIcon,
} from "@material-ui/icons";
import DataTable from "react-data-table-component";
import { API_GET, API_POST } from "../../services/api.service";
import CustomInput from "components/CustomInput/CustomInput.jsx";
import CircularProgress from "@material-ui/core/CircularProgress";

// Custom theme for professional look
const theme = createTheme({
  palette: {
    primary: {
      main: "#1976d2",
      light: "#42a5f5",
      dark: "#1565c0",
    },
    secondary: {
      main: "#dc004e",
    },
    background: {
      default: "#f5f7fa",
      paper: "#ffffff",
    },
  },
  typography: {
    fontFamily: "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif",
    h5: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 600,
    },
    subtitle1: {
      fontWeight: 500,
    },
  },
  shape: {
    borderRadius: 8,
  },
});

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    backgroundColor: "#f5f7fa",
    minHeight: "100vh",
    fontFamily: "'Inter', sans-serif",
  },
  headerContainer: {
    marginBottom: theme.spacing(4),
  },
  pageTitle: {
    fontWeight: 700,
    color: "#1a237e",
    marginBottom: theme.spacing(0.5),
  },
  pageSubtitle: {
    color: "#5f6368",
    fontSize: "0.875rem",
  },
  filterCard: {
    marginBottom: theme.spacing(3),
    borderRadius: 12,
    boxShadow: "0 4px 20px 0 rgba(0,0,0,0.05)",
    border: "1px solid #e0e0e0",
  },
  filterHeader: {
    padding: theme.spacing(2, 3),
    backgroundColor: "#f8f9fa",
    borderBottom: "1px solid #e0e0e0",
  },
  filterContent: {
    padding: theme.spacing(3),
  },
  controlGrid: {
    marginBottom: theme.spacing(2),
  },
  datePicker: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 8,
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: "#1976d2",
      },
    },
  },
  userSelect: {
    minWidth: 300,
    "& .MuiOutlinedInput-root": {
      borderRadius: 8,
    },
  },
  actionButtons: {
    display: "flex",
    gap: theme.spacing(1.5),
    justifyContent: "flex-end",
    marginTop: theme.spacing(2),
  },
  button: {
    borderRadius: 8,
    padding: theme.spacing(1, 2.5),
    fontWeight: 500,
    textTransform: "none",
    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
    "&:hover": {
      boxShadow: "0 4px 8px rgba(0,0,0,0.15)",
    },
  },
  primaryButton: {
    backgroundColor: "#1976d2",
    color: "#ffffff",
    "&:hover": {
      backgroundColor: "#1565c0",
    },
  },
  secondaryButton: {
    backgroundColor: "#ffffff",
    color: "#1976d2",
    border: "1px solid #1976d2",
    "&:hover": {
      backgroundColor: "#f5f7fa",
    },
  },
  tableSection: {
    marginTop: theme.spacing(3),
  },
  userSection: {
    marginBottom: theme.spacing(4),
    "&:last-child": {
      marginBottom: 0,
    },
  },
  userHeader: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(2, 0),
    marginBottom: theme.spacing(2),
  },
  userAvatar: {
    backgroundColor: "#1976d2",
    marginRight: theme.spacing(2),
    width: 40,
    height: 40,
  },
  userInfo: {
    flex: 1,
  },
  userName: {
    fontWeight: 600,
    color: "#1a237e",
  },
  userStats: {
    display: "flex",
    gap: theme.spacing(2),
    marginTop: theme.spacing(1),
    flexWrap: "wrap",
  },
  statChip: {
    backgroundColor: "#e3f2fd",
    color: "#1976d2",
    fontWeight: 500,
    borderRadius: 16,
  },
  tableWrapper: {
    borderRadius: 12,
    overflow: "hidden",
    boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
    border: "1px solid #e0e0e0",
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: 400,
  },
  headerCell: {
    whiteSpace: "normal !important",
    wordWrap: "break-word",
    lineHeight: "1.2",
    padding: "12px 8px !important",
    "& div": {
      whiteSpace: "normal",
      wordWrap: "break-word",
      lineHeight: "1.2",
    },
  },
  tooltipCell: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    "& span": {
      flex: 1,
    },
  },
}));

// Custom styled DataTable component with improved headers
const StyledDataTable = ({ columns, data, loading }) => {
  const customStyles = {
    headRow: {
      style: {
        backgroundColor: "#f5f7fa",
        borderBottom: "2px solid #e0e0e0",
        fontWeight: 600,
        minHeight: "80px", // Increased height for multi-line headers
      },
    },
    headCells: {
      style: {
        fontSize: "12px",
        fontWeight: 600,
        color: "#1a237e",
        padding: "8px 4px", // Reduced padding
        textTransform: "uppercase",
        letterSpacing: "0.3px",
        whiteSpace: "pre-wrap", // Changed from "normal" to "pre-wrap"
        wordWrap: "break-word",
        overflow: "visible", // Ensure content is visible
        lineHeight: "1.2",
        minHeight: "unset",
        "&:hover": {
          backgroundColor: "#edf4fc",
        },
      },
    },
    cells: {
      style: {
        fontSize: "13px",
        padding: "12px 8px",
        color: "#424242",
        borderRight: "1px solid #f0f0f0",
        "&:last-child": {
          borderRight: "none",
        },
        whiteSpace: "normal",
        wordWrap: "break-word",
      },
    },
    rows: {
      style: {
        minHeight: "52px",
        "&:not(:last-of-type)": {
          borderBottom: "1px solid #f0f0f0",
        },
        "&:hover": {
          backgroundColor: "#f8f9fa",
        },
      },
      stripedStyle: {
        backgroundColor: "#fafafa",
      },
    },
    pagination: {
      style: {
        borderTop: "1px solid #e0e0e0",
        padding: "16px",
        fontSize: "13px",
      },
    },
  };

  return (
    <DataTable
      columns={columns}
      data={data}
      progressPending={loading}
      progressComponent={
        <div style={{ padding: "60px", textAlign: "center" }}>
          <CircularProgress />
          <Typography style={{ marginTop: "16px", color: "#666" }}>
            Loading data...
          </Typography>
        </div>
      }
      customStyles={customStyles}
      pagination
      paginationPerPage={10}
      paginationRowsPerPageOptions={[10, 25, 50, 100]}
      highlightOnHover
      striped
      dense={false}
      noDataComponent={
        <div style={{ padding: "60px", textAlign: "center" }}>
          <Typography variant="body1" color="textSecondary">
            No records found for the selected criteria
          </Typography>
          <Typography variant="body2" color="textSecondary" style={{ marginTop: "8px" }}>
            Try adjusting your filters or date range
          </Typography>
        </div>
      }
    />
  );
};

export default function UserReporting(props) {
  const classes = useStyles();
  const { user = {}, IsCallDetailsOpen = false, callDetailsQuery = {} } = props;

  const [fromDate, setFromDate] = useState(new Date().toISOString().slice(0, 10));
  const [toDate, setToDate] = useState(new Date().toISOString().slice(0, 10));
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [dateChanage, setDateChanage] = useState(false);
  const [userDetails, setUserDetails] = useState({});
  const [users, setUsers] = useState([]);

  // Enhanced columns with better formatting and multi-line headers
  const columns = [
    {
      name: (
        <div className={classes.tooltipCell}>
          <span>Date</span>
          <Tooltip title="Date of activity" arrow>
            <InfoIcon fontSize="small" style={{ opacity: 0.6, fontSize: "14px" }} />
          </Tooltip>
        </div>
      ),
      selector: (row) => row.date,
      sortable: true,
      width: "100px",
      wrap: true,
      format: (row) => new Date(row.date).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      }),
      cell: (row) => (
        <Typography variant="body2" style={{ fontWeight: 500 }}>
          {new Date(row.date).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
          })}
        </Typography>
      ),
    },
    {
      name: (
        <div className={classes.tooltipCell}>
          <span>User</span>
          <Tooltip title="User name (max 12 characters)" arrow>
            <InfoIcon fontSize="small" style={{ opacity: 0.6, fontSize: "14px" }} />
          </Tooltip>
        </div>
      ),
      selector: (row) => row.user,
      sortable: true,
      width: "120px",
      wrap: true,
      cell: (row) => (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Avatar style={{ width: 28, height: 28, fontSize: 12, backgroundColor: "#1976d2" }}>
            {row.user && row.user.charAt(0).toUpperCase() || "U"}
          </Avatar>
          <div>
            <Typography variant="body2" style={{ fontWeight: 500 }}>
              {row.user || "Unknown"}
            </Typography>
          </div>
        </div>
      ),
    },
    {
      name: (
        <div style={{ 
          whiteSpace: "normal", 
          wordWrap: "break-word",
          lineHeight: "1.2"
        }}>
          Login Time
        </div>
      ),
      selector: (row) => row.LoginTime,
      sortable: true,
      width: "110px",
      wrap: true,
      cell: (row) => (
        <Typography variant="body2" style={{ fontFamily: "'Roboto Mono', monospace" }}>
          {row.LoginTime}
        </Typography>
      ),
    },
    {
      name: (
        <div style={{ 
          whiteSpace: "normal", 
          wordWrap: "break-word",
          lineHeight: "1.2"
        }}>
          Logout Time
        </div>
      ),
      selector: (row) => row.LogOutTime,
      sortable: true,
      width: "110px",
      wrap: true,
      cell: (row) => (
        <Typography variant="body2" style={{ fontFamily: "'Roboto Mono', monospace" }}>
          {row.LogOutTime}
        </Typography>
      ),
    },
    {
      name: (
        <div style={{ 
          whiteSpace: "normal", 
          wordWrap: "break-word",
          lineHeight: "1.2"
        }}>
          Working Hours
        </div>
      ),
      selector: (row) => row.workingHour,
      sortable: true,
      width: "120px",
      wrap: true,
      cell: (row) => {
        const hours = parseFloat(row.workingHour) || 0;
        return (
          <Chip
            label={`${hours.toFixed(1)}h`}
            size="small"
            style={{
              backgroundColor: hours >= 8 ? "#e8f5e9" : 
                             hours >= 6 ? "#fff3e0" : "#ffebee",
              color: hours >= 8 ? "#2e7d32" : 
                    hours >= 6 ? "#ef6c00" : "#c62828",
              fontWeight: 600,
              fontSize: "12px",
              minWidth: "60px",
            }}
          />
        );
      },
    },
    {
      name: "Activity",
      selector: (row) => row.activity,
      sortable: true,
      width: "100px",
      wrap: true,
      cell: (row) => {
        const activity = parseInt(row.activity) || 0;
        return (
          <Chip
            label={`${activity}`}
            size="small"
            style={{
              fontWeight: 600,
              fontSize: "11px",
              minWidth: "50px",
            }}
          />
        );
      },
    },
    {
      name: (
        <div style={{ 
          whiteSpace: "normal", 
          wordWrap: "break-word",
          lineHeight: "1.2"
        }}>
          Unique Prospects
        </div>
      ),
      selector: (row) => row.uniqueCalls,
      sortable: true,
      width: "120px",
      wrap: true,
      cell: (row) => (
        <Typography variant="body2" align="center" style={{ fontWeight: 600 }}>
          {row.uniqueCalls || 0}
        </Typography>
      ),
    },
    {
      name: (
        <div style={{ 
          whiteSpace: "normal", 
          wordWrap: "break-word",
          lineHeight: "1.2"
        }}>
          Total Calls
        </div>
      ),
      selector: (row) => row.totalCalls,
      sortable: true,
      width: "100px",
      wrap: true,
      cell: (row) => (
        <Typography variant="body2" align="center" style={{ fontWeight: 600, color: "#1976d2" }}>
          {row.totalCalls || 0}
        </Typography>
      ),
    },

    {
      name: (
        <div style={{ 
          whiteSpace: "normal", 
          wordWrap: "break-word",
          lineHeight: "1.2"
        }}>
          Business Calls
        </div>
      ),
      selector: (row) => row.businessCalls,
      sortable: true,
      width: "120px",
      wrap: true,
      cell: (row) => (
        <Typography variant="body2" align="center" style={{ fontWeight: 500 }}>
          {row.businessCalls || 0}
        </Typography>
      ),
    },
    {
      name: (
        <div style={{ 
          whiteSpace: "normal", 
          wordWrap: "break-word",
          lineHeight: "1.2"
        }}>
          Personal Calls
        </div>
      ),
      selector: (row) => row.personalCall,
      sortable: true,
      width: "120px",
      wrap: true,
      cell: (row) => (
        <Typography variant="body2" align="center" style={{ fontWeight: 500 }}>
          {row.personalCall || 0}
        </Typography>
      ),
    },
    {
      name: (
        <div style={{ 
          whiteSpace: "normal", 
          wordWrap: "break-word",
          lineHeight: "1.2"
        }}>
          Outgoing Calls
        </div>
      ),
      selector: (row) => row.outgoingCalls,
      sortable: true,
      width: "120px",
      wrap: true,
      cell: (row) => (
        <Typography variant="body2" align="center" style={{ fontWeight: 500 }}>
          {row.outgoingCalls || 0}
        </Typography>
      ),
    },
    {
      name: (
        <div style={{ 
          whiteSpace: "normal", 
          wordWrap: "break-word",
          lineHeight: "1.2"
        }}>
          Incoming Calls
        </div>
      ),
      selector: (row) => row.incomingCalls,
      sortable: true,
      width: "120px",
      wrap: true,
      cell: (row) => (
        <Typography variant="body2" align="center" style={{ fontWeight: 500 }}>
          {row.incomingCalls || 0}
        </Typography>
      ),
    },
    {
      name: (
        <div style={{ 
          whiteSpace: "normal", 
          wordWrap: "break-word",
          lineHeight: "1.2"
        }}>
          Connected Calls
        </div>
      ),
      selector: (row) => row.connectedCalls,
      sortable: true,
      width: "120px",
      wrap: true,
      cell: (row) => (
        <div style={{ textAlign: "center" }}>
          <Typography variant="body2" style={{ fontWeight: 600, color: "#2e7d32" }}>
            {row.connectedCalls || 0}
          </Typography>
          {row.totalCalls > 0 && (
            <Typography variant="caption" style={{ color: "#666", fontSize: "10px" }}>
              {Math.round((row.connectedCalls / row.totalCalls) * 100)}%
            </Typography>
          )}
        </div>
      ),
    },

    {
      name: (
        <div style={{ 
          whiteSpace: "normal", 
          wordWrap: "break-word",
          lineHeight: "1.2"
        }}>
          Recordings
        </div>
      ),
      selector: (row) => row.recordings,
      sortable: true,
      width: "100px",
      wrap: true,
      cell: (row) => (
        <Typography variant="body2" align="center" style={{ fontWeight: 500 }}>
          {row.recordings || 0}
        </Typography>
      ),
    },
    {
      name: (
        <div style={{ 
          whiteSpace: "normal", 
          wordWrap: "break-word",
          lineHeight: "1.2"
        }}>
          Talk Time (min)
        </div>
      ),
      selector: (row) => row.talkTime,
      sortable: true,
      width: "120px",
      wrap: true,
      cell: (row) => {
        return (
          <div style={{ textAlign: "center" }}>
            <Typography variant="body2" style={{ fontWeight: 600, color: "#7b1fa2" }}>
            {row.talkTime}
            </Typography>
          </div>
        );
      },
    },
  ];

  const loadTeam = async () => {
    if (!user.admin) return;
    try {
      let userInfo = await API_GET(`register/GetAllMembersByUserId?id=${user.admin_id}`);
      if (userInfo.success && userInfo.data.teams_membership) {
        const details = {};
        userInfo.data.teams_membership.forEach((m) => {
          details[m.username] = m.first_name;
        });
        setUserDetails(details);

        const teamList = userInfo.data.teams_membership
          .filter((m) => m.is_active)
          .map((m) => ({
            label: `${m.first_name} (${m.username})`,
            value: m.username,
            name: m.first_name,
          }));

        setUsers(teamList);

        if (user.role !== "owner") {
          const currentUser = user.username;
          setSelectedUsers([currentUser]);
          loadData([currentUser],details);
        } else {
          const defaultSelected = teamList.map((m) => m.value);
          setSelectedUsers(defaultSelected);
          loadData(defaultSelected,details);
        }
      }
    } catch (error) {
      console.error("Error loading team:", error);
    }
  };

  const loadData = async (teamSelected = [user.admin],details) => {
    setLoading(true);
    try {
      const res = await API_POST("report/userAggregateReporting", {
        admin: user.admin,
        startDate: fromDate,
        endDate: toDate,
        multiUser: selectedUsers.length ? selectedUsers : teamSelected,
      });

      if (res.data) {
        const processedData = res.data.map((item) => {
          
          const talkTime = parseInt(item.talkTime) || 0;
          const hours = Math.floor(talkTime / 3600);
          const minutes = Math.floor((talkTime % 3600) / 60);
          const seconds = talkTime % 60;

          return {
            ...item,
            user: ( details ? details[item.user] : (userDetails[item.user] || item.user || "Unknown")).substring(0, 12),
            // Convert workingHour to number for better sorting
            
            workingHour: parseFloat(item.workingHour) || 0,
            LoginTime: item.LoginTime && window.moment(item.LoginTime).format('DD-MM-YYYY hh:mm') || "--:--",
            LogoutTime: item.LogoutTime && window.moment(item.LogoutTime).format('DD-MM-YYYY hh:mm') || "--:--",
            // Ensure all numeric fields are numbers
          
            totalCalls: parseInt(item.totalCalls) || 0,
            businessCalls: parseInt(item.businessCalls) || 0,
            outgoingCalls: parseInt(item.outgoingCalls) || 0,
            incomingCalls: parseInt(item.incomingCalls) || 0,
            connectedCalls: parseInt(item.connectedCalls) || 0,
            activity: parseInt(item.activity) || 0,
            recordings: parseInt(item.recordings) || 0,
            talkTime: (hours ? hours+"h ": "") +minutes+"m "+seconds+"s" || 0
          }
        }).filter(item => item.totalCalls > 0);
        setRecords(processedData);
      } else {
        setRecords([]);
      }
    } catch (err) {
      console.error("Error loading data:", err);
      setRecords([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadTeam();
  }, [user]);

  const handleSearch = () => {
    loadData();
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const downloadFile = await API_POST(`action/ExportExcel`, {
        querydata: { admin: user.admin },
        body: {
          static: records,
          admin: user.admin,
          user: user.username,
          page: "summaryReports",
        },
      });

      if (downloadFile.status === 200) {
        alert("Export process started. Please check the system log section.");
      }
    } catch (error) {
      console.error("Export error:", error);
      alert("Export failed. Please try again.");
    } finally {
      setExporting(false);
    }
  };

  const handleReset = () => {
    setFromDate(new Date().toISOString().slice(0, 10));
    setToDate(new Date().toISOString().slice(0, 10));
    if (user.role !== "owner") {
      setSelectedUsers([user.username]);
    } else {
      const defaultSelected = users.map((u) => u.value);
      setSelectedUsers(defaultSelected);
    }
  };

  const calculateUserStats = (userData) => {
    const stats = {
      totalCalls: 0,
      totalProspects: 0,
      avgWorkingHours: 0,
      connectedCalls: 0,
      totalDays: userData.length,
      totalTalkTime: 0,
    };

    userData.forEach((item) => {
      stats.totalCalls += parseInt(item.totalCalls) || 0;
      stats.totalProspects += parseInt(item.totalProspects) || 0;
      stats.connectedCalls += parseInt(item.connectedCalls) || 0;
      stats.totalTalkTime += parseInt(item.talkTime) || 0;
    });

    if (userData.length > 0) {
      const totalHours = userData.reduce((sum, item) => {
        return sum + (parseFloat(item.workingHour) || 0);
      }, 0);
      stats.avgWorkingHours = (totalHours / userData.length).toFixed(1);
    }

    return stats;
  };

  return (
    <ThemeProvider theme={theme}>
      <div className={classes.root}>
        {/* Header Section */}
        <Box className={classes.headerContainer} style={{display:"flex"}}>
          <div>
          <Typography variant="h5" className={classes.pageTitle}>
            User Performance Report
          </Typography>
          <Typography variant="body2" className={classes.pageSubtitle}>
            Monitor and analyze user activities, calls, and productivity metrics
          </Typography>
          </div>

          <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
        {/* Date Range */}
        <Box flex={1} mr={2} style={{ marginTop: "-25px"}}>
       
          <FormControl fullWidth variant="outlined" size="small">
         
            <CustomInput
              style="dashboard_button"
              id="startdate"
              labelText=""
              labelProps={{ shrink: false }}
              formControlProps={{
                fullWidth: true,
                className: classes.datePicker,
                margin: "dense",
              }}
              inputProps={{
                type: "date_range",
                initialDateRange: {
                  startDate: fromDate,
                  endDate: toDate,
                },
                showFetch: false,
                onChange: (e) => {
                  setFromDate(e.startDate);
                  setToDate(e.endDate);
                  setDateChanage(true);
                 
                },
              }}
            />
          </FormControl>
        </Box>

        {/* Action Buttons */}
        <Box display="flex" gap={1}>
          <Button
            variant="contained"
            onClick={handleSearch}
            className={`${classes.button} ${classes.primaryButton}`}
            startIcon={<SearchIcon />}
            disabled={loading}
            size="medium"
          >
            {loading ? "Searching..." : "Search"}
          </Button>
          <Button
          style={{ marginLeft: "10px"}}
            variant="contained"
            onClick={handleExport}
            className={`${classes.button} ${classes.primaryButton}`}
            startIcon={exporting ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
            disabled={exporting || records.length === 0}
            size="medium"
          >
            {exporting ? "Exporting..." : "Export"}
          </Button>
        </Box>
      </Box>

        </Box>



        {/* Data Section */}
        <Box className={classes.tableSection}>
          {loading && records.length === 0 ? (
            <Box className={classes.loadingContainer}>
              <CircularProgress />
              <Typography style={{ marginLeft: "16px", color: "#666" }}>
                Loading user data...
              </Typography>
            </Box>
          ) : records.length > 0 ? (
            Object.entries(
              records.reduce((acc, item) => {
                const usr = !dateChanage ? user.admin : item.user || "Unknown";
                if (!acc[usr]) acc[usr] = [];
                acc[usr].push(item);
                return acc;
              }, {})
            ).map(([usr, data]) => {
              const stats = calculateUserStats(data);
              return (
                <Fade in key={usr}>
                  <Box className={classes.userSection}>
                    {/* User Header with Stats */}
                    <Box className={classes.userHeader}>
                      <Avatar className={classes.userAvatar}>
                        {usr.charAt(0).toUpperCase()}
                      </Avatar>
                      <Box className={classes.userInfo}>
                        <Typography variant="h6" className={classes.userName}>
                          {usr}
                        </Typography>
                        <Box className={classes.userStats}>
                     
                          <Chip
                            label={`${stats.totalCalls} Total Calls`}
                            className={classes.statChip}
                            size="small"
                          />
                          <Chip
                            label={`${stats.connectedCalls} Connected`}
                            className={classes.statChip}
                            size="small"
                          />
                        
                        </Box>
                      </Box>
                    </Box>

                    {/* Data Table */}
                    <Paper className={classes.tableWrapper}>
                      <StyledDataTable
                        columns={columns}
                        data={data}
                        loading={false}
                      />
                    </Paper>
                  </Box>
                </Fade>
              );
            })
          ) : !loading && (
            <Card className={classes.filterCard}>
              <CardContent style={{ textAlign: "center", padding: "60px 20px" }}>
                <Typography variant="h6" color="textSecondary" gutterBottom>
                  No Data Available
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Select a date range and users to view performance data
                </Typography>
              </CardContent>
            </Card>
          )}
        </Box>
      </div>
    </ThemeProvider>
  );
}